import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import React, { useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import {
  TimezoneAbbreviationEnum,
  SetRequiredNonNullable,
  ServiceTitanScheduledAppointment
} from '_lib/api/apiTypes.shim';

import { CircularProgress } from '_lib/components/CircularProgress/CircularProgress';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { FieldIssueProps } from '_lib/utils/validationErrorUtils';

import { ServiceTitanAppointmentDisplay } from '../ServiceTitanAppointmentDisplay/ServiceTitanAppointmentDisplay';

export type PreviousJobSelectProps = FieldIssueProps & {
  companyId: string;
  providerCustomerId?: string;
  providerLocationId?: string;
  required: boolean;
  disabled?: boolean;
  timezone: TimezoneAbbreviationEnum;
  onSetServiceTitanAppointment: (
    previousServiceTitanAppointment: ServiceTitanScheduledAppointment
  ) => Promise<void> | void;
  /* Display Props */
  previousServiceTitanAppointment: ServiceTitanScheduledAppointment | undefined;
  onClearPreviousServiceTitanAppointment: () => void;
};

/**
 * NOTE - to be clear, 'Jobs' we select are actually ServiceTitanAppointments, so we are actually 'selecting' a ServiceTitanAppointment, not a job
 */
const PreviousJobSelectEnabled = (
  props: SetRequiredNonNullable<PreviousJobSelectProps, 'providerCustomerId' | 'providerLocationId'>
) => {
  const {
    companyId,
    providerCustomerId,
    providerLocationId,
    required,
    disabled,
    timezone,
    onSetServiceTitanAppointment,
    previousServiceTitanAppointment,
    onClearPreviousServiceTitanAppointment,
    issues,
    issuePath
  } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = api.serviceTitan(companyId);

  const serviceTitanScheduledAppointmentsQuery = serviceTitanApi.customers
    .scheduledAppointments(providerCustomerId)
    .useUnpagedListCached(timezone);

  if (serviceTitanScheduledAppointmentsQuery.error) {
    return (
      <ManualErrorBoundary
        titleVariant='body1'
        errorState={{ message: 'Failed to load Customer Jobs' }}
        onRefresh={async () => {
          await serviceTitanScheduledAppointmentsQuery.refetch();
        }}
      />
    );
  }

  const filteredServiceTitanAppointments =
    serviceTitanScheduledAppointmentsQuery?.data?.filter(
      (appointment) => appointment.job.providerLocationId === providerLocationId
    ) || [];

  if (previousServiceTitanAppointment) {
    return (
      <FlexBox sx={{ flexDirection: 'column' }}>
        <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='body1Bold' text='JOB' />
          <Tooltip title='Clear Job' placement='bottom'>
            <IconButton onClick={() => onClearPreviousServiceTitanAppointment()} sx={{ padding: 0 }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <ServiceTitanAppointmentDisplay
          serviceTitanAppointment={previousServiceTitanAppointment}
          job={previousServiceTitanAppointment.job}
          timezone={timezone}
        />
      </FlexBox>
    );
  }

  return (
    <TextField
      fullWidth
      label='Job'
      required={required}
      disabled={serviceTitanScheduledAppointmentsQuery.isFetching || disabled}
      defaultValue={''}
      select
      error={{
        issues,
        issuePath
      }}
      onChange={(e) => {
        if (e.target.value) {
          const previousServiceTitanAppointmentProviderId = e.target.value;

          const previousServiceTitanAppointment = filteredServiceTitanAppointments.find(
            (o) => o.providerId === previousServiceTitanAppointmentProviderId
          );

          if (!previousServiceTitanAppointment) return;

          onSetServiceTitanAppointment(previousServiceTitanAppointment);
        }
      }}
      slotProps={{
        input: {
          endAdornment: serviceTitanScheduledAppointmentsQuery.isFetching ? (
            <IconButton size='small' sx={{ paddingRight: 3 }} disabled disableRipple>
              <SyncIcon sx={(theme) => ({ color: theme.palette.primary.main })} />
              {serviceTitanScheduledAppointmentsQuery.isFetching && (
                <CircularProgress sx={{ position: 'absolute', zIndex: 1 }} size={30} />
              )}
            </IconButton>
          ) : undefined
        }
      }}>
      {filteredServiceTitanAppointments.length > 0 &&
        filteredServiceTitanAppointments.map((serviceTitanAppointment) => {
          return (
            <MenuItem key={serviceTitanAppointment.providerId} value={serviceTitanAppointment.providerId}>
              <ServiceTitanAppointmentDisplay
                serviceTitanAppointment={serviceTitanAppointment}
                job={serviceTitanAppointment.job}
                timezone={timezone}
              />
            </MenuItem>
          );
        })}
      {filteredServiceTitanAppointments.length === 0 && <MenuItem disabled>No Jobs found.</MenuItem>}
    </TextField>
  );
};

export const PreviousJobSelectDisabled = (props: { required: PreviousJobSelectProps['required'] }) => {
  return (
    <FlexBox width={'100%'}>
      <Tooltip title={'Location must be selected first'} followCursor={true}>
        <span style={{ width: 'inherit' }}>
          <TextField fullWidth select disabled label='Job' required={props.required}>
            <MenuItem disabled>No Jobs found.</MenuItem>
          </TextField>
        </span>
      </Tooltip>
    </FlexBox>
  );
};

export const PreviousJobSelect = (props: PreviousJobSelectProps) => {
  const { providerCustomerId, providerLocationId, ...restProps } = props;

  return providerCustomerId && providerLocationId ? (
    <PreviousJobSelectEnabled
      providerCustomerId={providerCustomerId}
      providerLocationId={providerLocationId}
      {...restProps}
    />
  ) : (
    <PreviousJobSelectDisabled required={props.required} />
  );
};
